import { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Chip, IconButton, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material';
import { Dot } from '../Dot';
import { useTranslation } from 'react-i18next';

export interface ChipSelectOption {
  name: string;
  color: string;
  value: string | number;
  textColor?: string;
}

interface ChipSelectProps {
  value?: string;
  options: ChipSelectOption[];
  backgroundColor: string;
  textColor?: string;
  onSelect?: (value: string | number) => void;
  linkMode?: boolean;
  showColorDot?: boolean;
}

export const ChipSelect = ({
  value,
  backgroundColor,
  textColor,
  onSelect,
  options,
  linkMode,
  showColorDot,
}: ChipSelectProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (val?: string | number) => {
    val && onSelect && onSelect(val);
    setAnchorEl(null);
  };

  return (
    <div>
      {linkMode ? (
        <>
          {value ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  maxWidth: 190,
                  cursor: 'pointer',
                }}
                onClick={handleClick}
              >
                {showColorDot && <Dot color={backgroundColor} />}
                <Tooltip title={value}>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="body2"
                  >
                    {value}
                  </Typography>
                </Tooltip>
              </Box>
              <IconButton size="small" onClick={handleClick}>
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Button sx={{ height: 20 }} size="small" onClick={handleClick} color="inherit" variant="text">
                <Typography textTransform="initial" variant="body2" color="text.disabled">
                  {t('common.unassigned')}
                </Typography>
              </Button>
              <IconButton size="small" onClick={handleClick}>
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </>
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Chip
            size="small"
            label={value}
            sx={{
              backgroundColor: backgroundColor,
              color: textColor,
              '& .MuiChip-label': {
                color: textColor,
              },
            }}
          />
          <IconButton size="small" onClick={handleClick}>
            <ArrowDropDownIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()}
        PaperProps={{ sx: { [theme.breakpoints.down('md')]: {
          maxWidth: 'calc(100% - 32px)',
          '& .MuiMenuItem-root': { whiteSpace: 'normal', minHeight: 44 },
          '& .MuiChip-root': { minWidth: 0, height: 'auto', maxWidth: '100%' },
          '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 },
        } } }}>

        {options?.map((option) => {
          const isSelected = value === option.value || value === option.name;
          return (
            <MenuItem key={option.name} onClick={() => handleClose(option.value)}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ width: '100%', minWidth: 200 }}
              >
                <Chip
                  size="small"
                  label={option.name}
                  sx={{
                    backgroundColor: option.color,
                    color: option.textColor || theme.palette.common.white,
                    '& .MuiChip-label': {
                      color: option.textColor || theme.palette.common.white,
                    },
                  }}
                />
                {isSelected && (
                  <CheckIcon
                    fontSize="small"
                    sx={{
                      color: 'primary.main',
                      ml: 'auto',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};
